// Parser local determinístico para listas de produtos no formato canônico:
//   DESCRIÇÃO [DE PRECO_ANTIGO POR PRECO_ATUAL] [/UN | /KG] [(OBSERVACOES)]
//   DESCRIÇÃO CARTÃO PRECO PIX PRECO /UN
//
// + heurística `listaPareceBaguncada` para decidir se vale chamar IA.

const ROTULOS_PAGAMENTO = [
  'PIX',
  'CARTAO', 'CARTÃO',
  'DEBITO', 'DÉBITO',
  'CREDITO', 'CRÉDITO',
  'BOLETO',
  'A VISTA', 'À VISTA', 'AVISTA',
  'A PRAZO', 'À PRAZO',
];

const ERROS_ORTOGRAFICOS = [
  'MUSARELA', 'MUSSARELA', 'XAMPU', 'CALABREZA', 'CALABRESSA',
  'KILO', 'QUILO', 'LITROS', 'MELANSIA', 'MILANSEA',
];

const PRECO_RE = /(\d+[.,]\d{2})|(\d+\s+(?:E|E\s+)\s*\d+)|(\b\d+\s*REAIS?\b)/i;
const PRECO_NUMERICO_RE = /(\d+[.,]\d{2})/g;

function normalizarPreco(p) {
  if (!p) return p;
  return String(p).replace('.', ',');
}

function detectarUnidade(linha) {
  const m = linha.match(/\/\s*(UN|KG|GR|L|ML|G|CX|PCT)\b/i);
  if (m) {
    const u = m[1].toUpperCase();
    if (u === 'UN') return 'unidade';
    if (u === 'KG') return 'kg';
    if (u === 'L') return 'l';
    if (u === 'ML') return 'ml';
    if (u === 'G' || u === 'GR') return 'cada'; // grama, conforme select
    if (u === 'CX') return 'caixa';
    if (u === 'PCT') return 'pacote';
  }
  if (/\bKG\b|\bQUILO\b|\bKILO\b/i.test(linha)) return 'kg';
  if (/\bLITROS?\b/i.test(linha)) return 'l';
  if (/\bMILILITROS?\b|\bML\b/i.test(linha)) return 'ml';
  return '';
}

function extrairObs(linha) {
  // Tudo entre parênteses no final
  const m = linha.match(/\(([^)]+)\)\s*$/);
  if (m) {
    return { obs: m[1].trim(), linhaSemObs: linha.replace(m[0], '').trim() };
  }
  return { obs: '', linhaSemObs: linha.trim() };
}

// Se a observação for apenas uma porção/peso (com ou sem "A CADA"),
// movê-la para o NOME do produto. Assim o áudio passa a falar o peso.
// Ex.: nome="ALHO ROXO", obs="CADA 100G"  -> nome="ALHO ROXO A CADA 100G", obs=""
// Ex.: nome="CARNE MOÍDA", obs="500G"     -> nome="CARNE MOÍDA 500G",      obs=""
function promoverPesoDaObsParaNome(nome, obs) {
  if (!obs) return { nome, obs };
  const t = obs.trim();
  // 1) "A CADA 100G" / "CADA 100 G" / "A CADA 1KG"
  let m = t.match(/^(?:A\s+)?CADA\s+(\d+(?:[.,]\d+)?)\s*(KG|G|ML|L|LT|LTS)\b\.?$/i);
  if (m) {
    const peso = `${m[1]}${m[2].toUpperCase()}`;
    return { nome: `${nome} A CADA ${peso}`.replace(/\s+/g, ' ').trim(), obs: '' };
  }
  // 2) "100G" sozinho
  m = t.match(/^(\d+(?:[.,]\d+)?)\s*(KG|G|ML|L|LT|LTS)\b\.?$/i);
  if (m) {
    const peso = `${m[1]}${m[2].toUpperCase()}`;
    return { nome: `${nome} ${peso}`.replace(/\s+/g, ' ').trim(), obs: '' };
  }
  return { nome, obs };
}

function detectarRotuloPagamento(textoAntesDoPreco) {
  const upper = textoAntesDoPreco.toUpperCase();
  for (const r of ROTULOS_PAGAMENTO) {
    if (upper.endsWith(r) || upper.endsWith(r + ' ')) return r;
    if (upper.includes(' ' + r + ' ') || upper.includes(' ' + r)) {
      // Pega a última ocorrência
      return r;
    }
  }
  return null;
}

/**
 * Tenta extrair produtos do texto.
 * Retorna array de:
 *   { nome, tipopreco, preco, precode, precopor,
 *     labelPrecoPrincipal, labelPrecoSecundario,
 *     precoPrincipal, precoSecundario,
 *     observacao, unidade }
 */
export function parserLocalProdutos(texto) {
  if (!texto || typeof texto !== 'string') return [];

  const linhas = texto
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean);

  const produtos = [];

  for (const raw of linhas) {
    const { obs, linhaSemObs } = extrairObs(raw);
    if (!linhaSemObs) continue;

    // Encontrar todos preços numéricos formato 9,99 ou 9.99
    const precos = [];
    let m;
    const re = new RegExp(PRECO_NUMERICO_RE.source, 'g');
    while ((m = re.exec(linhaSemObs)) !== null) {
      precos.push({ valor: normalizarPreco(m[1]), index: m.index, len: m[0].length });
    }

    // Sem preço numérico canônico -> não conseguimos parsear
    if (precos.length === 0) continue;

    const unidade = detectarUnidade(linhaSemObs);
    // Limpa o rótulo de unidade (/UN /KG /GR /G /L /ML /CX /PCT) da string de trabalho
    const textoTrabalho = linhaSemObs.replace(/\/\s*(UN|KG|GR|G|L|ML|CX|PCT)\b/gi, '').trim();

    if (precos.length === 1) {
      const p = precos[0];
      const antes = textoTrabalho.substring(0, p.index).trim();
      const rotulo = detectarRotuloPagamento(antes);
      // Remove o rótulo do nome se foi detectado no final
      let nome = antes;
      if (rotulo) {
        const reRot = new RegExp('\\s*' + rotulo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i');
        nome = nome.replace(reRot, '').trim();
      }
      const promovido = promoverPesoDaObsParaNome(nome || textoTrabalho, obs);
      produtos.push({
        nome: promovido.nome,
        tipopreco: 'simples',
        preco: p.valor,
        labelPrecoPrincipal: rotulo || 'R$',
        precoPrincipal: p.valor,
        observacao: promovido.obs,
        unidade,
      });
      continue;
    }

    if (precos.length >= 2) {
      const p1 = precos[0];
      const p2 = precos[1];
      const antesP1 = textoTrabalho.substring(0, p1.index).trim();
      const entreP1P2 = textoTrabalho.substring(p1.index + p1.len, p2.index).trim();

      const upperAntes = antesP1.toUpperCase();
      const upperEntre = entreP1P2.toUpperCase();

      let tipopreco = 'dexpory';
      let labelA = 'DE';
      let labelB = 'POR';
      let nome = antesP1;

      // Caso "DE … POR …"
      if (/\bDE\b/.test(upperAntes) && /\bPOR\b/.test(upperEntre)) {
        const idxDe = upperAntes.lastIndexOf('DE');
        nome = antesP1.substring(0, idxDe).trim();
        labelA = 'DE';
        labelB = 'POR';
        tipopreco = 'dexpory';
      } else {
        // Caso pagamento (CARTÃO X PIX Y, etc.)
        const rot1 = detectarRotuloPagamento(antesP1);
        const rot2 = detectarRotuloPagamento(entreP1P2);
        if (rot1 && rot2) {
          // Remove rótulo do nome
          const reRot = new RegExp('\\s*' + rot1.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i');
          nome = antesP1.replace(reRot, '').trim();
          labelA = rot1;
          labelB = rot2;
          tipopreco = 'pagamento';
        } else if (rot2) {
          labelA = 'DE';
          labelB = rot2;
          tipopreco = 'pagamento';
        }
        // senão mantém DE/POR
      }

      const promovido2 = promoverPesoDaObsParaNome(nome || textoTrabalho, obs);
      produtos.push({
        nome: promovido2.nome,
        tipopreco,
        precode: tipopreco === 'dexpory' ? p1.valor : undefined,
        precopor: tipopreco === 'dexpory' ? p2.valor : undefined,
        preco: tipopreco === 'pagamento' ? p2.valor : p2.valor,
        labelPrecoPrincipal: labelA,
        labelPrecoSecundario: labelB,
        precoPrincipal: p1.valor,
        precoSecundario: p2.valor,
        observacao: promovido2.obs,
        unidade,
      });
    }
  }

  return produtos;
}

/**
 * Heurística — se algum sinal bater, força chamada à IA mesmo que o parser
 * tenha encontrado alguns produtos.
 */
export function listaPareceBaguncada(texto, produtosExtraidos = []) {
  if (!texto) return false;
  const t = texto.toUpperCase();

  const linhasTexto = texto.split(/\r?\n/).filter(l => l.trim().length > 0);
  const qtdLinhas = linhasTexto.length;

  // Preço por extenso
  if (/\bREAIS?\b|\bCENTAVOS?\b|\bREAL\b/.test(t)) return true;

  // Unidades por extenso
  if (/\bKILO\b|\bQUILO\b|\bLITROS?\b/.test(t)) return true;

  // Observações fora de parêntese (incluindo erros de digitação comuns)
  if (/\bLIMITE\b|\bLIMITI\b|\bLIMTE\b|\bMAX\b|\bMÁX\b|\bMÁXIMO\b|\bMAXIMO\b|\bVALIDADE\b|\bVENCIMENTO\b|\bENQUANTO DURAR\b|\bPOR CLIENTE\b|\bPOR CRIENTE\b|\bPOR CPF\b|\bSOMENTE HOJE\b/.test(t)) {
    // Só conta se NÃO está dentro de parênteses em todas as ocorrências
    const semParens = texto.replace(/\([^)]*\)/g, '');
    if (/\bLIMITE\b|\bLIMITI\b|\bLIMTE\b|\bMAX\b|\bMÁX\b|\bMÁXIMO\b|\bMAXIMO\b|\bVALIDADE\b|\bVENCIMENTO\b|\bENQUANTO DURAR\b|\bPOR CLIENTE\b|\bPOR CRIENTE\b|\bPOR CPF\b|\bSOMENTE HOJE\b/i.test(semParens)) {
      return true;
    }
  }

  // Datas DD/MM/AAAA
  if (/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/.test(texto)) return true;

  // Forma de pagamento mas sem 2 preços numéricos canônicos na mesma linha
  for (const linha of linhasTexto) {
    const upper = linha.toUpperCase();
    const temPgto = ROTULOS_PAGAMENTO.some(r => upper.includes(r));
    if (temPgto) {
      const matches = linha.match(/\d+[.,]\d{2}/g) || [];
      if (matches.length < 2) return true;
    }
  }

  // Linha com 3+ preços (provável união de produtos)
  for (const linha of linhasTexto) {
    const matches = linha.match(/\d+[.,]\d{2}/g) || [];
    if (matches.length >= 3) return true;
  }

  // Erros ortográficos comuns
  for (const e of ERROS_ORTOGRAFICOS) {
    if (t.includes(e)) return true;
  }

  // Muitas linhas mas poucos produtos extraídos
  if (qtdLinhas >= 3 && produtosExtraidos.length < qtdLinhas * 0.5) return true;

  // Nenhum preço numérico canônico = bagunçado
  if (qtdLinhas > 0 && !PRECO_NUMERICO_RE.test(texto)) return true;

  return false;
}
